import { useEffect, useRef, useState } from 'react'
import { retrieveLaunchParams } from '@telegram-apps/sdk'
import { TelegramInitData } from '@/types'
import { getMockTelegramData } from '@/lib/mockTelegram'

// Интерфейс для возвращаемого значения хука
interface UseTelegramReturn {
  isReady: boolean
  isInTelegram: boolean
  webApp: any
  user?: any
  initData?: string
  getInitData: () => TelegramInitData | null
  getStartParam: () => string | null
  themeParams?: any
  colorScheme?: string
  hapticFeedback: {
    impact: (style?: 'light' | 'medium' | 'heavy') => void
    notification: (type: 'error' | 'success' | 'warning') => void
    selection: () => void
  }
  mainButton: {
    setText: (text: string) => void
    onClick: (callback: () => void) => void
    offClick: (callback: () => void) => void
    show: () => void
    hide: () => void
    enable: () => void
    disable: () => void
    showProgress: () => void
    hideProgress: () => void
  }
  backButton: {
    onClick: (callback: () => void) => void
    offClick: (callback: () => void) => void
    show: () => void
    hide: () => void
  }
  requestContact: (callback: (contact: { phone_number: string }) => void) => void
  showAlert: (message: string, callback?: () => void) => void
  showConfirm: (message: string, callback: (confirmed: boolean) => void) => void
  setHeaderColor: (color: string) => void
  setBackgroundColor: (color: string) => void
  enableClosingConfirmation: () => void
  disableClosingConfirmation: () => void
  close: () => void
}

type TelegramLaunchFallback = {
  raw: string
  initData: TelegramInitData & { raw: string }
  startParam?: string
  version?: string
  platform?: string
  themeParams?: any
}

let cachedLaunchFallback: TelegramLaunchFallback | null | undefined

function getTelegramLaunchFallback(): TelegramLaunchFallback | null {
  if (cachedLaunchFallback !== undefined) return cachedLaunchFallback

  try {
    const launchParams = retrieveLaunchParams()
    const raw = launchParams.initDataRaw
    const initData = launchParams.initData
    const user = initData?.user

    if (!raw || !initData || !user) {
      cachedLaunchFallback = null
      return cachedLaunchFallback
    }

    cachedLaunchFallback = {
      raw,
      initData: {
        raw,
        user: {
          id: user.id,
          first_name: user.firstName,
          last_name: user.lastName,
          tg_username: user.username,
          language_code: user.languageCode,
          is_premium: user.isPremium,
          photo_url: user.photoUrl
        },
        chat_instance: initData.chatInstance,
        chat_type: initData.chatType,
        start_param: initData.startParam || launchParams.startParam,
        auth_date: Math.floor(initData.authDate.getTime() / 1000),
        hash: initData.hash
      },
      startParam: launchParams.startParam || initData.startParam,
      version: launchParams.version,
      platform: launchParams.platform,
      themeParams: launchParams.themeParams
    }
  } catch {
    cachedLaunchFallback = null
  }

  return cachedLaunchFallback
}

declare global {
  interface Window {
    Telegram: {
      WebApp: {
        initData: string
        initDataUnsafe: {
          user?: {
            id: number
            first_name: string
            last_name?: string
            username?: string  // Telegram отправляет 'username', не 'tg_username'
            language_code?: string
            is_premium?: boolean
            photo_url?: string
          }
          chat_instance?: string
          chat_type?: string
          start_param?: string
          auth_date: number
          hash: string
        }
        version: string
        platform: string
        colorScheme: 'light' | 'dark'
        themeParams: {
          bg_color?: string
          text_color?: string
          hint_color?: string
          link_color?: string
          button_color?: string
          button_text_color?: string
          secondary_bg_color?: string
        }
        isExpanded: boolean
        viewportHeight: number
        viewportStableHeight: number
        MainButton: {
          text: string
          color: string
          textColor: string
          isVisible: boolean
          isActive: boolean
          isProgressVisible: boolean
          setText: (text: string) => void
          onClick: (callback: () => void) => void
          offClick: (callback: () => void) => void
          show: () => void
          hide: () => void
          enable: () => void
          disable: () => void
          showProgress: (leaveActive?: boolean) => void
          hideProgress: () => void
        }
        BackButton: {
          isVisible: boolean
          onClick: (callback: () => void) => void
          offClick: (callback: () => void) => void
          show: () => void
          hide: () => void
        }
        HapticFeedback: {
          impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void
          notificationOccurred: (type: 'error' | 'success' | 'warning') => void
          selectionChanged: () => void
        }
        expand: () => void
        close: () => void
        ready: () => void
        requestContact: (callback: (contact: { phone_number: string }) => void) => void
        showAlert: (message: string, callback?: () => void) => void
        showConfirm: (message: string, callback: (confirmed: boolean) => void) => void
        showPopup: (params: { title?: string; message: string; buttons?: Array<{ id?: string; type?: string; text: string }> }, callback?: (buttonId: string) => void) => void
        setHeaderColor?: (color: string) => void
        setBackgroundColor?: (color: string) => void
        enableClosingConfirmation?: () => void
        disableClosingConfirmation?: () => void
      }
    }
  }
}

export function useTelegram(): UseTelegramReturn {
  const [isReady, setIsReady] = useState(false)
  const launchFallback = typeof window !== 'undefined' ? getTelegramLaunchFallback() : null
  const [webApp, setWebApp] = useState<typeof window.Telegram.WebApp | null>(() => {
    if (typeof window === 'undefined') return null
    return window.Telegram?.WebApp ?? null
  })
  const hasCalledReady = useRef(false)
  const hasResolvedEnvironment = useRef(false)

  useEffect(() => {
    const isLikelyTelegramLaunch = (() => {
      if (typeof window === 'undefined') return false

      const url = new URL(window.location.href)
      const hasTelegramParams =
        Array.from(url.searchParams.keys()).some((key) => key.startsWith('tgWebApp')) ||
        window.location.hash.includes('tgWebApp')
      const hasTelegramReferrer = /t\.me|telegram\.me/i.test(document.referrer)
      const hasTelegramUserAgent = /Telegram/i.test(navigator.userAgent)

      return hasTelegramParams || hasTelegramReferrer || hasTelegramUserAgent
    })()

    const initializeTelegramWebApp = (tg: typeof window.Telegram.WebApp | null): boolean => {
      if (!tg) return false

      const hasInitData = Boolean(tg.initData && tg.initData.length > 0)
      const hasUser = Boolean(tg.initDataUnsafe?.user?.id)

      if (!hasInitData && !hasUser) {
        return false
      }

      setWebApp(tg)

      if (!hasCalledReady.current) {
        hasCalledReady.current = true
        tg.ready()
        tg.expand()

        if (typeof (tg as any).disableVerticalSwipes === 'function') {
          (tg as any).disableVerticalSwipes()
        }
      }

      setIsReady(true)
      hasResolvedEnvironment.current = true
      return true
    }

    if (typeof window !== 'undefined' && initializeTelegramWebApp(window.Telegram?.WebApp ?? null)) {
      return
    }

    if (launchFallback) {
      hasResolvedEnvironment.current = true
      setIsReady(true)
    } else if (!isLikelyTelegramLaunch) {
      hasResolvedEnvironment.current = true
      setIsReady(true)
      return
    }

    const startedAt = Date.now()
    const waitTimeoutMs = 10000
    const pollIntervalMs = 100
    let timer: number | null = null

    const resolveAsBrowserFallback = () => {
      if (timer !== null) {
        window.clearInterval(timer)
        timer = null
      }
      if (hasResolvedEnvironment.current) return
      hasResolvedEnvironment.current = true
      setIsReady(true)
    }

    const retryInitialization = () => {
      if (initializeTelegramWebApp(window.Telegram?.WebApp ?? null)) {
        if (timer !== null) {
          window.clearInterval(timer)
          timer = null
        }
        return
      }

      if (Date.now() - startedAt >= waitTimeoutMs) {
        resolveAsBrowserFallback()
      }
    }

    const telegramScript = document.querySelector<HTMLScriptElement>('script[src*="telegram-web-app.js"]')
    const handleTelegramScriptLoad = () => retryInitialization()
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        retryInitialization()
      }
    }
    const handleFocus = () => retryInitialization()

    telegramScript?.addEventListener('load', handleTelegramScriptLoad)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)

    timer = window.setInterval(retryInitialization, pollIntervalMs)

    return () => {
      if (timer !== null) {
        window.clearInterval(timer)
      }
      telegramScript?.removeEventListener('load', handleTelegramScriptLoad)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
    }
  }, [launchFallback])

  const runtimeWebApp = typeof window !== 'undefined' ? window.Telegram?.WebApp ?? null : null
  const activeWebApp = webApp || runtimeWebApp
  const hasActiveInitData = Boolean(activeWebApp?.initData && activeWebApp.initData.length > 0)
  const hasTelegramUser = Boolean(activeWebApp?.initDataUnsafe?.user?.id)
  const isTelegramUserAgent = typeof navigator !== 'undefined' && /Telegram/i.test(navigator.userAgent)
  const isLikelyTelegramEnvironment = Boolean(
    launchFallback || (activeWebApp && (hasActiveInitData || hasTelegramUser || isTelegramUserAgent))
  )

  // Страховка: если activeWebApp доступен через window, но useEffect не вызвал ready()/expand()
  useEffect(() => {
    if (!hasActiveInitData || hasCalledReady.current || !activeWebApp) return
    hasCalledReady.current = true
    activeWebApp.ready()
    activeWebApp.expand()
    if (typeof (activeWebApp as any).disableVerticalSwipes === 'function') {
      (activeWebApp as any).disableVerticalSwipes()
    }
    console.log('✅ useTelegram: ready()/expand() called via safety-net useEffect')
  }, [hasActiveInitData, activeWebApp])

  // Если это не Telegram-среда (обычный браузер), возвращаем fallback-данные.
  // ⚠️ Мок-данные существуют ТОЛЬКО в dev-сборке: они подписаны `mock-hash`, сервер такую
  // подпись отвергает (401 «Invalid Telegram signature»). В прод-сборке здесь обязан быть
  // null — иначе браузерный вход выглядит как Telegram-вход и упирается в отказ сервера.
  if (!isLikelyTelegramEnvironment && isReady) {
    const mockData = import.meta.env.DEV ? getMockTelegramData() : null
    return {
      isReady: true,
      isInTelegram: false,
      webApp: null,
      user: mockData?.user,
      initData: '',
      getInitData: () => mockData,
      getStartParam: () => null,
      themeParams: {},
      colorScheme: 'light',
      hapticFeedback: {
        impact: () => {},
        notification: () => {},
        selection: () => {}
      },
      mainButton: {
        setText: () => {},
        onClick: () => {},
        offClick: () => {},
        show: () => {},
        hide: () => {},
        enable: () => {},
        disable: () => {},
        showProgress: () => {},
        hideProgress: () => {}
      },
      backButton: {
        onClick: () => {},
        offClick: () => {},
        show: () => {},
        hide: () => {}
      },
      requestContact: () => {},
      showAlert: (message: string) => {
        alert(message)
      },
      showConfirm: (message: string, callback: (confirmed: boolean) => void) => {
        callback(confirm(message))
      },
      setHeaderColor: () => {},
      setBackgroundColor: () => {},
      enableClosingConfirmation: () => {},
      disableClosingConfirmation: () => {},
      close: () => {}
    }
  }

  const getInitData = (): TelegramInitData | null => {
    console.log('🔍 useTelegram getInitData: called, webApp state:', webApp)
    console.log('🔍 useTelegram getInitData: webApp exists:', !!webApp)
    console.log('🔍 useTelegram getInitData: direct window check:', !!(typeof window !== 'undefined' && window.Telegram?.WebApp))
    
    if (!activeWebApp) {
      if (launchFallback) {
        console.log('✅ useTelegram getInitData: using launch params fallback')
        return launchFallback.initData
      }
      console.log('❌ useTelegram getInitData: no webApp (state, window, or launch params fallback)')
      return null
    }
    
    console.log('✅ useTelegram getInitData: using webApp:', activeWebApp === webApp ? 'from state' : 'from window')
    
    const data = activeWebApp.initDataUnsafe
    console.log('🔍 useTelegram getInitData: initDataUnsafe:', data)
    
    // Более гибкая проверка - проверяем наличие пользователя
    if (!data || !data.user) {
      console.log('❌ useTelegram getInitData: no data or user:', { data, user: data?.user })
      return null
    }

    console.log('✅ useTelegram getInitData: user found:', data.user)

    // Если нет auth_date или hash, но есть пользователь - создаем минимальные данные
    const result = {
      user: data.user,
      chat_instance: data.chat_instance,
      chat_type: data.chat_type,
      start_param: data.start_param,
      auth_date: data.auth_date || Math.floor(Date.now() / 1000), // Используем текущее время если нет
      hash: data.hash || 'mock_hash' // Используем mock hash если нет
    }
    
    console.log('✅ useTelegram getInitData: returning result:', result)
    return result
  }

  const getStartParam = (): string | null => {
    console.log('🔍 useTelegram getStartParam: called with universal approach')
    
    // 1) официальный способ — query-параметр
    const url = new URL(window.location.href)
    let p = url.searchParams.get('tgWebAppStartParam') || url.searchParams.get('startapp')
    console.log('🔍 getStartParam: from URL query params:', p)
    if (p) return p

    // 2) launch params прямо из Telegram
    const twa = (window as any).Telegram?.WebApp
    p = twa?.initParams?.tgWebAppStartParam
    console.log('🔍 getStartParam: from initParams.tgWebAppStartParam:', p)
    if (p) return p

    // 3) старые свойства клиента
    p = twa?.startParam || twa?.start_param
    console.log('🔍 getStartParam: from startParam/start_param:', p)
    if (p) return p

    // 4) дубль в initDataUnsafe
    p = twa?.initDataUnsafe?.start_param ?? null
    console.log('🔍 getStartParam: from initDataUnsafe.start_param:', p)
    if (p) return p

    // 5) крайний случай: кто-то положил в hash
    const hashParams = new URLSearchParams(window.location.hash.slice(1))
    p = hashParams.get('tgWebAppStartParam') || hashParams.get('startapp') || null
    console.log('🔍 getStartParam: from hash params:', p)

    if (!p && launchFallback?.startParam) {
      p = launchFallback.startParam
      console.log('🔍 getStartParam: from launch params fallback:', p)
    }
    
    console.log('🔍 getStartParam: final result:', p)
    return p
  }

  const hapticFeedback = {
    impact: (style: 'light' | 'medium' | 'heavy' = 'medium') => {
      try {
        activeWebApp?.HapticFeedback.impactOccurred(style)
      } catch (e) {
        // HapticFeedback not supported
      }
    },
    notification: (type: 'error' | 'success' | 'warning') => {
      try {
        activeWebApp?.HapticFeedback.notificationOccurred(type)
      } catch (e) {
        // HapticFeedback not supported
      }
    },
    selection: () => {
      try {
        activeWebApp?.HapticFeedback.selectionChanged()
      } catch (e) {
        // HapticFeedback not supported
      }
    }
  }

  const mainButton = {
    setText: (text: string) => {
      try {
        activeWebApp?.MainButton?.setText(text)
      } catch (e) {
        // MainButton not available
      }
    },
    onClick: (callback: () => void) => {
      try {
        activeWebApp?.MainButton?.onClick(callback)
      } catch (e) {
        // MainButton not available
      }
    },
    offClick: (callback: () => void) => {
      try {
        activeWebApp?.MainButton?.offClick(callback)
      } catch (e) {
        // MainButton not available
      }
    },
    show: () => {
      try {
        activeWebApp?.MainButton?.show()
      } catch (e) {
        // MainButton not available
      }
    },
    hide: () => {
      try {
        activeWebApp?.MainButton?.hide()
      } catch (e) {
        // MainButton not available
      }
    },
    enable: () => {
      try {
        activeWebApp?.MainButton?.enable()
      } catch (e) {
        // MainButton not available
      }
    },
    disable: () => {
      try {
        activeWebApp?.MainButton?.disable()
      } catch (e) {
        // MainButton not available
      }
    },
    showProgress: () => {
      try {
        activeWebApp?.MainButton?.showProgress()
      } catch (e) {
        // MainButton not available
      }
    },
    hideProgress: () => {
      try {
        activeWebApp?.MainButton?.hideProgress()
      } catch (e) {
        // MainButton not available
      }
    },
  }

  const backButton = {
    onClick: (callback: () => void) => {
      try {
        activeWebApp?.BackButton?.onClick(callback)
      } catch (e) {
        // BackButton not available
      }
    },
    offClick: (callback: () => void) => {
      try {
        activeWebApp?.BackButton?.offClick(callback)
      } catch (e) {
        // BackButton not available
      }
    },
    show: () => {
      try {
        activeWebApp?.BackButton?.show()
      } catch (e) {
        // BackButton not available
      }
    },
    hide: () => {
      try {
        activeWebApp?.BackButton?.hide()
      } catch (e) {
        // BackButton not available
      }
    },
  }

  const requestContact = (callback: (contact: { phone_number: string }) => void) => {
    try {
      activeWebApp?.requestContact(callback)
    } catch (e) {
      // requestContact not available
      console.warn('requestContact not supported')
    }
  }

  const showAlert = (message: string, callback?: () => void) => {
    try {
      activeWebApp?.showAlert(message, callback)
    } catch (error) {
      // Fallback для старых версий Telegram WebApp
      alert(message)
      callback?.()
    }
  }

  const showConfirm = (message: string, callback: (confirmed: boolean) => void) => {
    try {
      // showConfirm использует showPopup внутри, который доступен только с версии 6.1+
      const supportsPopup = activeWebApp && activeWebApp.version && parseFloat(activeWebApp.version) >= 6.1
      
      if (activeWebApp?.showConfirm && supportsPopup) {
        activeWebApp.showConfirm(message, callback)
      } else {
        // Fallback для старых версий или браузера
        const confirmed = confirm(message)
        callback(confirmed)
      }
    } catch (error) {
      // Fallback при ошибке
      console.warn('showConfirm failed, using confirm()', error)
      const confirmed = confirm(message)
      callback(confirmed)
    }
  }

  const close = () => {
    try {
      activeWebApp?.close()
    } catch (e) {
      // close not available
    }
  }

  return {
    isReady,
    isInTelegram: isLikelyTelegramEnvironment,
    webApp: activeWebApp,
    user: activeWebApp?.initDataUnsafe.user || launchFallback?.initData.user,
    initData: activeWebApp?.initData || launchFallback?.raw,
    getInitData,
    getStartParam,
    themeParams: activeWebApp?.themeParams || launchFallback?.themeParams,
    colorScheme: activeWebApp?.colorScheme,
    hapticFeedback,
    mainButton,
    backButton,
    requestContact,
    showAlert,
    showConfirm,
    setHeaderColor: (color: string) => {
      try {
        if (activeWebApp?.setHeaderColor) {
          activeWebApp.setHeaderColor(color)
        }
      } catch (e) {
        // setHeaderColor not available
      }
    },
    setBackgroundColor: (color: string) => {
      try {
        if (activeWebApp?.setBackgroundColor) {
          activeWebApp.setBackgroundColor(color)
        }
      } catch (e) {
        // setBackgroundColor not available
      }
    },
    enableClosingConfirmation: () => {
      try {
        if (activeWebApp?.enableClosingConfirmation) {
          activeWebApp.enableClosingConfirmation()
        }
      } catch (e) {
        // enableClosingConfirmation not available
      }
    },
    disableClosingConfirmation: () => {
      try {
        if (activeWebApp?.disableClosingConfirmation) {
          activeWebApp.disableClosingConfirmation()
        }
      } catch (e) {
        // disableClosingConfirmation not available
      }
    },
    close
  }
}
